import { useQuery } from "@tanstack/react-query";
import { userInfoQueryOptions } from "./queries/auth";
import type { UserInfo } from "./http/auth";

export function canAccess(
  info: UserInfo,
  resource: string,
  actions?: Array<string>,
): boolean {
  if ("all" in info.permissions) return true;

  if (!(resource in info.permissions)) return false;

  if (!actions || actions.length === 0) return true;

  const resourceActions = new Set(info.permissions[resource]);

  return (
    resourceActions.has("all") || resourceActions.isSupersetOf(new Set(actions))
  );
}

export function useResourceAccessPermission(
  resource: string,
  actions?: Array<string>,
) {
  return useQuery({
    ...userInfoQueryOptions,
    select(data) {
      return canAccess(data, resource, actions);
    },
  });
}

export type PermGuard = {
  resource: string;
  actions?: Array<string>;
};

export function evaluatePermGuards(
  userInfo: UserInfo,
  guards: Array<PermGuard>,
  pathname?: string,
) {
  if (guards.length > 0) {
    const arePermGuardsValid = guards.every(({ resource, actions }) =>
      canAccess(userInfo, resource, actions),
    );
    if (!arePermGuardsValid) {
      if (import.meta.env.DEV && pathname) {
        console.warn(`Tried to acccess ${pathname} without necessary perms`);
      }
      return false;
    }
  }
  return true;
}
